import { useCallback, useState } from "react";
import { Spin } from "antd5";
import { Text } from "styles/utility-components";

import { CpvSummary } from "components/cpv_summary/CpvSummary";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { Line } from "lib/core_components/Line";
import { useBuyerCpvStats } from "lib/hooks/api/buyer/useBuyerCpvStatsGQL";
import { EventNames, useTracking } from "lib/tracking";

import css from "./SummaryTab.module.scss";

type Props = {
  buyerGuid: string;
  showMatchingExpiries: () => void;
};

const CpvLookbackDates: Record<string, string> = {
  P1Y: "1 year",
  P2Y: "2 years",
  P3Y: "3 years",
  P4Y: "4 years",
  P5Y: "5 years",
};

function SummarySection({ buyerGuid, showMatchingExpiries }: Props): JSX.Element {
  const { logEvent } = useTracking();
  const [upcomingExpiries, setUpcomingExpiries] = useState(false);
  const [cpvLookback, setCpvLookback] = useState("P5Y");
  const { data } = useBuyerCpvStats(buyerGuid, { period: cpvLookback, upcomingExpiries });

  const onShowMatchingExpiriesClicked = useCallback(() => {
    logEvent(EventNames.seeMatchingExpiriesClicked);
    showMatchingExpiries();
  }, [logEvent, showMatchingExpiries]);

  return (
    <>
      <div className={css.header}>
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger buttonType="tertiary">
              {upcomingExpiries ? "Upcoming contract expiries" : "Historical awards"}
            </DropdownMenuTrigger>
          }
          modal={false}
        >
          {["Upcoming contract expiries", "Historical awards"].map((option) => (
            <DropdownMenuItem
              key={option}
              onSelect={() => {
                const isExpiries = option === "Upcoming contract expiries";
                setUpcomingExpiries(isExpiries);
                logEvent(EventNames.summaryViewChanged, {
                  "View selected": option,
                });
              }}
            >
              {option}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        <Text>{upcomingExpiries ? "In the next" : "From the past"}</Text>
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger buttonType="tertiary">
              {CpvLookbackDates[cpvLookback]}
            </DropdownMenuTrigger>
          }
          modal={false}
          width="var(--radix-dropdown-menu-trigger-width)"
        >
          {Object.keys(CpvLookbackDates).map((d) => (
            <DropdownMenuItem
              key={d}
              onSelect={() => {
                logEvent(EventNames.summaryTimelineChanged, {
                  "View selected": CpvLookbackDates[d],
                });
                setCpvLookback(d);
              }}
            >
              {CpvLookbackDates[d]}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>
        {upcomingExpiries && (
          <>
            <Line vertical />
            <Button buttonType="tertiary" onClick={onShowMatchingExpiriesClicked}>
              See expiries matching my settings
            </Button>
          </>
        )}
      </div>
      <Line />
      <div className={css.summary}>
        {data ? (
          <CpvSummary
            requiredSlideoutType="BUYERS"
            overallStats={data.overallStats}
            perCpvStats={data.perCpvStats}
            buyerGuid={buyerGuid}
            dateFilter={
              upcomingExpiries
                ? { expiryDate: { filter: { relativeTo: cpvLookback, relativeFrom: "PT0S" } } }
                : {
                    publishDate: {
                      // the lookback date must be negated
                      relativeFrom: cpvLookback.replace(/P/, "P-"),
                      relativeTo: "PT0S",
                    },
                  }
            }
          />
        ) : (
          <div className={css.spinner}>
            <Spin />
          </div>
        )}
      </div>
    </>
  );
}

export default SummarySection;
